'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { DateRangeFilter, FilterPill, Popover } from '@terra/ui-design-system';
import { useDateFormatter } from '../../hooks/useDateFormatter';

type ListPageDateRangeFilterProps = {
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
};

export function ListPageDateRangeFilter({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: ListPageDateRangeFilterProps) {
  const tFilters = useTranslations('proxy.filters');
  const dateFormatter = useDateFormatter();
  const [isOpen, setIsOpen] = useState(false);
  const selectedDates = [startDate, endDate].filter(
    (date): date is Date => date !== null,
  );

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      positioning={{ placement: 'bottom-start', strategy: 'fixed' }}
      closeOnInteractOutside
    >
      <Popover.Trigger asChild>
        <FilterPill
          density="compact"
          placeholder={label}
          isActive={selectedDates.length > 0}
          selectedText={selectedDates
            .map((date) => dateFormatter.formatDateOnly(date))
            .join(' – ')}
          onClear={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onStartDateChange(null);
            onEndDateChange(null);
            setIsOpen(false);
          }}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content p="3" bg="bg.default" borderRadius="lg" boxShadow="xl">
          <DateRangeFilter
            size="sm"
            label={label}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            startPlaceholder={tFilters('startDate')}
            endPlaceholder={tFilters('endDate')}
          />
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
